import { useState, useCallback, useRef, useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import type { ScrollRailItem } from '@nula/core-ui';

export interface HeadingAnchor extends ScrollRailItem {
  /** ProseMirror document position of this heading node */
  pos: number;
}

interface UseHeadingAnchorsOptions {
  editor?: Editor | null;
  scrollElRef: React.RefObject<HTMLElement | null>;
}

interface UseHeadingAnchorsResult {
  items: ScrollRailItem[];
  activeId: string | null;
  _setAnchors: (anchors: HeadingAnchor[]) => void;
  jumpTo: (id: string) => void;
}

export function useHeadingAnchors({ editor, scrollElRef }: UseHeadingAnchorsOptions): UseHeadingAnchorsResult {
  const [anchors, setAnchors] = useState<HeadingAnchor[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const anchorsRef = useRef<HeadingAnchor[]>([]);

  const _setAnchors = useCallback((next: HeadingAnchor[]) => {
    // extractHeadings runs on EVERY keystroke and rebuilds this array fresh.
    // Bail out when nothing structurally changed so typing inside a paragraph
    // triggers ZERO re-renders here — otherwise every character re-rendered the
    // editor and re-subscribed the scroll-spy effect (which depends on
    // `anchors`), the dominant source of typing lag.
    const prev = anchorsRef.current;
    const unchanged =
      prev.length === next.length &&
      prev.every(
        (a, i) => a.id === next[i].id && a.label === next[i].label && a.pos === next[i].pos,
      );
    if (unchanged) return;
    anchorsRef.current = next;
    setAnchors(next);
  }, []);

  // Active heading tracking — uses coordsAtPos so coordinates are always in
  // the editor's own viewport frame, not affected by DOM layout complexity.
  useEffect(() => {
    const scrollEl = scrollElRef.current;
    if (!scrollEl || !editor) return;

    const onScroll = () => {
      const containerTop = scrollEl.getBoundingClientRect().top;
      const offset = 40;
      let current: string | null = null;

      anchorsRef.current.forEach((anchor) => {
        try {
          const coords = editor.view.coordsAtPos(anchor.pos);
          // coords.top is viewport-relative. Subtract containerTop to get
          // position relative to the scroll container's visible top.
          if (coords.top - containerTop - offset <= 0) {
            current = anchor.id;
          }
        } catch {
          // pos may be stale if document changed — skip silently
        }
      });

      setActiveId(current ?? (anchorsRef.current[0]?.id ?? null));
    };

    onScroll();
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, [editor, scrollElRef, anchors]);

  const jumpTo = useCallback((id: string) => {
    const anchor = anchorsRef.current.find((a) => a.id === id);
    if (!anchor || !editor) return;
    const scrollEl = scrollElRef.current;
    if (!scrollEl) return;
    // ProseMirror wipes any manually-set DOM attributes on reconcile, so we
    // can't rely on id attributes. Instead find the heading by matching its
    // exact text content among all rendered heading elements.
    const headings = Array.from(scrollEl.querySelectorAll<HTMLElement>('h1, h2, h3'));
    const el = headings.find((h) => h.textContent?.trim() === anchor.label);
    if (!el) return;
    const elRect = el.getBoundingClientRect();
    const containerRect = scrollEl.getBoundingClientRect();
    const targetScrollTop = scrollEl.scrollTop + (elRect.top - containerRect.top) - 24;
    scrollEl.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
    setActiveId(id);
  }, [editor, scrollElRef]);

  const items: ScrollRailItem[] = anchors.map(({ id, label }) => ({ id, label }));

  return { items, activeId, _setAnchors, jumpTo };
}
